import {CellRefColors} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/CellRefColors';
import {ClientFunction} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/ClientFunction';
import {ISelectionTextFocusActivator} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FocusActivator';
import {
    FormulaInputTokenType,
    ICellReferenceRangeExternalFormulaToken,
    ILinearFormulaToken
} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/FormulaToken';
import {MeshBounds} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshBounds';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshPoint} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshPoint';
import {MeshRange, ReferencedRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshSize';
import {MeshTypeaheadItem} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshTypeaheadItem';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {IList} from '@mintdata/mint-kit/api/models/List';
import {TextSelection} from '@mintdata/mint-kit/api/models/Text';
import {computed, observable} from 'mobx';

export class MeshInputState
{
    // @observable public isEditMode:boolean = false;
    @observable public location:MeshPoint = MeshPoint.zero();
    @observable public cellSize:MeshSize = MeshSize.zero();
    public inputActivator:ISelectionTextFocusActivator = null;

    @observable public activeCellBounds:MeshBounds = MeshBounds.from(0, 0, 0, 0);
    @observable public colIndex:number = null;
    @observable public rowIndex:number = null;
    @observable public formulaTokens:Array<ILinearFormulaToken> = null;
    @observable public textSelection:TextSelection = new TextSelection(0, null);
    @observable public isUserInput:boolean = false;
    @observable public isValueEdited:boolean = false;
    public isSelectAfterChange:boolean = false;
    public isSelectCellRefRangeMode:boolean = false;
    public selectCellRefStartLocation:MeshLocation = null;

    @observable public isTypeaheadOpen:boolean = false;
    @observable public typeaheadList:IList<MeshTypeaheadItem<any>> = null;

    @observable public isDocVisible:boolean = true;
    @observable public isDocCollapsed:boolean = false;

    @observable public docFunction:ClientFunction = null;

    @observable public isInputJustMounted:boolean = false;

    @computed
    public get isDocOpen():boolean
    {
        return !this.isTypeaheadOpen && !!this.currentFunctionToken;
    }

    @computed
    public get tokenAtCursor():ILinearFormulaToken
    {
        if( !this.formulaTokens )
            return null;

        for( const token of this.formulaTokens )
            if( token.token && this.textSelection.startIndex > token.token.startIndex
                && this.textSelection.startIndex <= token.token.endIndex )
                return token;
    }

    @computed
    public get tokenAtCursorMatches():Array<ILinearFormulaToken>
    {
        if( this.formulaTokens && this.tokenAtCursor && this.tokenAtCursor.matchTokenIndexes != null )
            return this.tokenAtCursor.matchTokenIndexes.map(
                (matchTokenIndex:number) => this.formulaTokens[matchTokenIndex],
            );

        return [];
    }

    @computed
    public get tokenBeforeTokenAtCursor():ILinearFormulaToken
    {
        if( this.formulaTokens && this.tokenAtCursor && this.tokenAtCursor.index > 0 )
            return this.formulaTokens[this.tokenAtCursor.index - 1];

        return null;
    }

    @computed
    public get tokenAfterTokenAtCursor():ILinearFormulaToken
    {
        if( this.formulaTokens && this.tokenAtCursor && this.tokenAtCursor.index < (this.formulaTokens.length - 1) )
            return this.formulaTokens[this.tokenAtCursor.index + 1];

        return null;
    }

    @computed
    public get token2AfterTokenAtCursor():ILinearFormulaToken
    {
        if( this.formulaTokens && this.tokenAfterTokenAtCursor && this.tokenAfterTokenAtCursor.index < (this.formulaTokens.length - 1) )
            return this.formulaTokens[this.tokenAfterTokenAtCursor.index + 1];

        return null;
    }

    @computed
    public get currentFunctionToken():ILinearFormulaToken
    {
        if( this.formulaTokens && this.tokenAtCursor && this.tokenAtCursor.functionTokenIndex != null )
            return this.formulaTokens[this.tokenAtCursor.functionTokenIndex];

        return null;
    }

    @computed
    public get tokenAfterFunction():ILinearFormulaToken
    {
        if( this.formulaTokens && this.currentFunctionToken )
            return this.formulaTokens[this.currentFunctionToken.index + 1];

        return null;
    }

    @computed
    public get token2AfterFunction():ILinearFormulaToken
    {
        if( this.formulaTokens && this.tokenAfterFunction )
            return this.formulaTokens[this.tokenAfterFunction.index + 1];

        return null;
    }

    @computed
    public get currentFunctionArgumentIndex():number
    {
        if( this.tokenAtCursor && this.currentFunctionToken )
            return this.tokenAtCursor.functionArgumentIndex;

        return null;
    }

    @computed
    public get currentFunctionTrailingLength():number
    {
        if( this.tokenAfterFunction )
            if( this.tokenAfterFunction.token.type === FormulaInputTokenType.ParenthesesStart )
                return 1;
            else if( this.tokenAfterFunction.token.type === FormulaInputTokenType.Space
                && this.token2AfterFunction
                && this.token2AfterFunction.token.type === FormulaInputTokenType.ParenthesesStart )
                return this.tokenAfterFunction.token.value.length + 1;

        return 0;
    }

    @computed
    public get currentCellRefToken():ILinearFormulaToken
    {
        if( this.tokenAtCursor
            && (this.tokenAtCursor.token.type === FormulaInputTokenType.CellReference
                || this.tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceExternal
                || this.tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRange
                || this.tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRangeExternal) )
            return this.tokenAtCursor;

        if( this.tokenAfterTokenAtCursor
            && (this.tokenAfterTokenAtCursor.token.type === FormulaInputTokenType.CellReference
                || this.tokenAfterTokenAtCursor.token.type === FormulaInputTokenType.CellReferenceExternal
                || this.tokenAfterTokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRange
                || this.tokenAfterTokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRangeExternal) )
            return this.tokenAfterTokenAtCursor;

        return null;
    }

    @computed
    public get currentReferencedRange():ReferencedRange
    {
        if( this.currentCellRefToken && this.referencedRanges )
        {
            const cellRefRangeToken:ICellReferenceRangeExternalFormulaToken = this.currentCellRefToken.token as ICellReferenceRangeExternalFormulaToken;

            const range:MeshRange = MeshUtils.getCellRefRangeFromR1C1(
                cellRefRangeToken.range,
                this.colIndex,
                this.rowIndex,
            );

            const rangeKey:string = (cellRefRangeToken.sheetName || '') + range.asKey();

            return this.referencedRanges.get(rangeKey);
        }

        return null;
    }

    @computed
    public get cellRefTokens():Array<ILinearFormulaToken>
    {
        if( this.formulaTokens )
            return this.formulaTokens.filter(
                (token:ILinearFormulaToken) =>
                    token.token.type === FormulaInputTokenType.CellReference
                    || token.token.type === FormulaInputTokenType.CellReferenceExternal
                    || token.token.type === FormulaInputTokenType.CellReferenceRange
                    || token.token.type === FormulaInputTokenType.CellReferenceRangeExternal,
            );

        return null;
    }

    @computed
    public get referencedRanges():Map<string, ReferencedRange>
    {
        const rangesMap:Map<string, ReferencedRange> = new Map<string, ReferencedRange>();

        if( this.cellRefTokens )
            this.cellRefTokens.forEach((token:ILinearFormulaToken) =>
            {
                const cellRefRangeToken:ICellReferenceRangeExternalFormulaToken = token.token as ICellReferenceRangeExternalFormulaToken;
                const range:MeshRange = MeshUtils.getCellRefRangeFromR1C1(
                    cellRefRangeToken.range,
                    this.colIndex,
                    this.rowIndex,
                );
                const rangeKey:string = (cellRefRangeToken.sheetName || '') + range.asKey();

                if( !rangesMap.has(rangeKey) )
                    rangesMap.set(rangeKey, new ReferencedRange(
                        range.from,
                        range.to,
                        CellRefColors.getColorByIndex(rangesMap.size),
                        cellRefRangeToken.sheetName
                    ));
            });

        return rangesMap;
    }

    private _isOperationalToken(token:ILinearFormulaToken, isFollowing:boolean = false):boolean
    {
        return token &&
            (token.token.type === FormulaInputTokenType.FormulaStart
                || token.token.type === FormulaInputTokenType.FormulaLazyStart
                || token.token.type === FormulaInputTokenType.FormulaLiveStart
                || token.token.type === FormulaInputTokenType.Comma
                || token.token.type === FormulaInputTokenType.Operand
                || token.token.type === FormulaInputTokenType.ParenthesesStart
                || (isFollowing
                    && (token.token.type === FormulaInputTokenType.ParenthesesEnd
                        || token.token.type === FormulaInputTokenType.Redundant
                        || token.token.type === FormulaInputTokenType.Unknown)));
    }

    @computed
    public get isSelectCellRefMode():boolean
    {
        return !this.isTypeaheadOpen && this.isUserInput &&
            ((this.tokenAtCursor
                && (this._isOperationalToken(this.tokenAtCursor)
                    || this.tokenAtCursor.token.type === FormulaInputTokenType.CellReference
                    || this.tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceExternal
                    || this.tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRange
                    || this.tokenAtCursor.token.type === FormulaInputTokenType.CellReferenceRangeExternal))
                    || (this.tokenAtCursor && this.tokenAtCursor.token.type === FormulaInputTokenType.Space
                    && this.tokenBeforeTokenAtCursor && this._isOperationalToken(this.tokenBeforeTokenAtCursor)))
            && (!this.tokenAfterTokenAtCursor
                || this._isOperationalToken(this.tokenAfterTokenAtCursor, true)
                || (this.tokenAfterTokenAtCursor && this.tokenAfterTokenAtCursor.token.type === FormulaInputTokenType.Space
                    && (!this.token2AfterTokenAtCursor || this._isOperationalToken(this.token2AfterTokenAtCursor, true))));
    }
}
